
import { LabelPrinterDto, LabelPrinterPrintLogCreateDto } from 'dtos'
import { loadBluetoothLabelPrintingConfig, recordLabelPrinterPrintLog } from './labelPrinting'

const LABEL_PRINTER_SERVICE_UUID = '38eb4a80-c570-11e3-9507-0002a5d5c51b'
const LABEL_PRINTER_CHARACTERISTIC_UUID = '38eb4a82-c570-11e3-9507-0002a5d5c51b'
const BLE_CHUNK_SIZE = 20
const INTER_CHUNK_DELAY_MS = 50
const PRE_WRITE_SETTLE_MS = 1000
const POST_WRITE_SETTLE_MS = 1000
const CONNECT_TIMEOUT_MS = 15_000
const GATT_DISCOVERY_TIMEOUT_MS = 10_000
const WRITE_TIMEOUT_MS = 5_000
const MAX_CONNECT_ATTEMPTS = 2
const GATT_DISCONNECTED_RETRY_DELAYS_MS = [500, 1500, 3000]

type WriteMethod = 'with-response' | 'without-response' | 'legacy'
type BluetoothOperationStage = 'connect' | 'service-discovery' | 'characteristic-discovery' | 'write'

type BluetoothLabelPrintingConfig = Awaited<ReturnType<typeof loadBluetoothLabelPrintingConfig>>

type BluetoothLabelPrintOptions = {
  getPrn: () => Promise<Uint8Array>
  labelTypeId?: number
  labelPrintContextTypeId?: number
  vehicleGuid?: string
}

type BluetoothPrintMetrics = {
  selectedBluetoothDeviceName?: string
  labelPrinter?: LabelPrinterDto
  writeMethodUsed?: WriteMethod
  totalChunks: number
  failedChunkIndex?: number
  failedByteOffset?: number
  bytesAttempted: number
  bytesSent: number
  bluetoothFailureStage?: BluetoothOperationStage
  gattConnectAttempts: number
  gattReconnectAttempts: number
  gattOperationRetries: number
}

type BluetoothLabelPrinterState = {
  config?: BluetoothLabelPrintingConfig
  configPromise?: Promise<BluetoothLabelPrintingConfig>
  cachedPrinterDevice?: BluetoothDeviceLike
  selectedLabelPrinter?: LabelPrinterDto
  printQueue: Promise<void>
}

type BluetoothCharacteristicPropertiesLike = {
  write?: boolean
  writeWithoutResponse?: boolean
}

type WritableBluetoothCharacteristic = {
  properties?: BluetoothCharacteristicPropertiesLike
  writeValueWithResponse?: (value: ArrayBuffer) => Promise<void>
  writeValueWithoutResponse?: (value: ArrayBuffer) => Promise<void>
  writeValue: (value: ArrayBuffer) => Promise<void>
}

type BluetoothServiceLike = {
  getCharacteristic: (uuid: string) => Promise<WritableBluetoothCharacteristic>
}

type BluetoothServerLike = {
  connected: boolean
  disconnect: () => void
  getPrimaryService: (uuid: string) => Promise<BluetoothServiceLike>
}

type BluetoothGattLike = {
  connected: boolean
  connect: () => Promise<BluetoothServerLike>
  disconnect: () => void
  getPrimaryService: (uuid: string) => Promise<BluetoothServiceLike>
}

type BluetoothDeviceLike = {
  name?: string
  gatt?: BluetoothGattLike
}

type BluetoothNavigatorLike = {
  getDevices?: () => Promise<BluetoothDeviceLike[]>
  requestDevice: (options: {
    filters: Array<{ name: string }>
    optionalServices: string[]
  }) => Promise<BluetoothDeviceLike>
}

class BluetoothWriteFailure {
  constructor(
    public method: WriteMethod,
    public error: unknown
  ) {}
}

const bluetoothLabelPrinterState: BluetoothLabelPrinterState = {
  printQueue: Promise.resolve()
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })
}

function isDomException(error: unknown): error is DOMException {
  return typeof DOMException !== 'undefined' && error instanceof DOMException
}

function getErrorMessage(error: unknown) {
  if (isDomException(error)) {
    return error.message || error.name
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Unknown error'
}

function getBluetooth() {
  if (typeof navigator === 'undefined') {
    return undefined
  }

  return (navigator as Navigator & { bluetooth?: BluetoothNavigatorLike }).bluetooth
}

function alertUser(message: string) {
  if (typeof window !== 'undefined') {
    window.alert(message)
  }
}

export async function initializeBluetoothLabelPrinterState() {
  if (!bluetoothLabelPrinterState.configPromise) {
    bluetoothLabelPrinterState.configPromise = loadBluetoothLabelPrintingConfig()
      .then(config => {
        bluetoothLabelPrinterState.config = config
        cachePreviouslyAllowedPrinter(config)
        return config
      })
      .catch(error => {
        bluetoothLabelPrinterState.configPromise = undefined
        throw error
      })
  }

  return bluetoothLabelPrinterState.configPromise
}

function getConfiguredPrinterBluetoothName(labelPrinter: LabelPrinterDto) {
  return labelPrinter.deviceName?.trim() || labelPrinter.serialNumber?.trim()
}

function getConfiguredPrinterDeviceNames(config: BluetoothLabelPrintingConfig) {
  return Array.from(
    new Set(
      config.activeLabelPrinters
        .map(getConfiguredPrinterBluetoothName)
        .filter((deviceName): deviceName is string => !!deviceName)
    )
  )
}

function getConfiguredLabelPrinterByDeviceName(
  config: BluetoothLabelPrintingConfig,
  deviceName: string | undefined
) {
  if (!deviceName) {
    return undefined
  }

  const normalizedDeviceName = deviceName.trim()

  return config.activeLabelPrinters.find(
    labelPrinter => getConfiguredPrinterBluetoothName(labelPrinter) === normalizedDeviceName
  )
}

function isConfiguredLabelPrinter(device: BluetoothDeviceLike | undefined, config: BluetoothLabelPrintingConfig) {
  return !!getConfiguredLabelPrinterByDeviceName(config, device?.name)
}

function disconnectDevice(device: BluetoothDeviceLike | undefined) {
  try {
    if (device?.gatt?.connected) {
      device.gatt.disconnect()
    }
  } catch {
    // Best effort only; some browsers throw when disconnecting an already closed session.
  }
}

async function withTimeout<T>(operation: Promise<T>, timeoutMs: number, timeoutMessage: string) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(timeoutMessage))
    }, timeoutMs)
  })

  try {
    return await Promise.race([operation, timeout])
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

function isUnsupportedWriteError(error: unknown) {
  if (!isDomException(error)) {
    return false
  }

  return error.name === 'NotSupportedError'
}

function getBluetoothWriteFailure(error: unknown) {
  return error instanceof BluetoothWriteFailure ? error : undefined
}

function getBluetoothWriteFailureError(error: unknown) {
  return getBluetoothWriteFailure(error)?.error ?? error
}

function isGattDisconnectedError(error: unknown) {
  const message = getErrorMessage(error)

  return (
    /gatt server is disconnected/i.test(message)
    || /cannot perform gatt operations/i.test(message)
    || /device\.gatt\.connect/i.test(message)
  )
}

function getPlatformProfile() {
  const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent

  if (/Android/i.test(userAgent)) {
    if (/SamsungBrowser|SAMSUNG|SM-[A-Z0-9]|SCH-|SGH-|GT-/i.test(userAgent)) {
      return 'Samsung Android'
    }

    return 'Chrome Android'
  }

  return 'Browser'
}

function shouldForceBluetoothDeviceSelection() {
  return true
}

function getPrnPreview(prn: Uint8Array) {
  return Array.from(prn.subarray(0, 256))
    .map(byte => String.fromCharCode(byte))
    .join('')
}

function getErrorName(error: unknown) {
  if (isDomException(error)) {
    return error.name
  }

  if (error instanceof Error) {
    return error.name
  }

  return undefined
}

function getErrorStack(error: unknown) {
  return error instanceof Error ? error.stack : undefined
}

function buildPrintLog(
  options: Partial<BluetoothLabelPrintOptions>,
  config: BluetoothLabelPrintingConfig | undefined,
  prn: Uint8Array | undefined,
  metrics: Partial<BluetoothPrintMetrics>,
  wasSuccessful: boolean,
  error?: unknown
) {
  const prnPreview = prn ? getPrnPreview(prn) : ''
  const labelTypeGuid = options.labelTypeId ? config?.labelTypeGuidById.get(options.labelTypeId) : undefined
  const labelPrintContextTypeGuid = options.labelPrintContextTypeId
    ? config?.labelPrintContextTypeGuidById.get(options.labelPrintContextTypeId)
    : undefined

  const dto = new LabelPrinterPrintLogCreateDto()
  dto.labelPrinterGuid = metrics.labelPrinter?.labelPrinterGuid
  dto.labelTypeGuid = labelTypeGuid
  dto.vehicleGuid = options.vehicleGuid
  dto.labelPrintContextTypeGuid = labelPrintContextTypeGuid
  dto.selectedBluetoothDeviceName = metrics.selectedBluetoothDeviceName
  dto.wasSuccessful = wasSuccessful
  dto.errorMessage = error ? getErrorMessage(error) : undefined
  dto.errorName = error ? getErrorName(error) : undefined
  dto.errorStack = error ? getErrorStack(error) : undefined
  dto.browserUserAgent = typeof navigator === 'undefined' ? undefined : navigator.userAgent
  dto.bluetoothServiceUuid = LABEL_PRINTER_SERVICE_UUID
  dto.bluetoothCharacteristicUuid = LABEL_PRINTER_CHARACTERISTIC_UUID
  dto.platformProfile = getPlatformProfile()
  dto.writeMethodUsed = metrics.writeMethodUsed
  dto.chunkSize = BLE_CHUNK_SIZE
  dto.chunkDelayMs = INTER_CHUNK_DELAY_MS
  dto.finalPrintDrainDelayMs = POST_WRITE_SETTLE_MS
  dto.totalChunks = metrics.totalChunks
  dto.prnLooksLikeZpl = prnPreview.includes('^XA')
  dto.prnLooksLikeCpcl = prnPreview.trimStart().startsWith('!')
  dto.statusProbeAttempted = false
  dto.statusProbeSucceeded = false
  dto.failedChunkIndex = metrics.failedChunkIndex
  dto.failedByteOffset = metrics.failedByteOffset
  dto.bytesAttempted = metrics.bytesAttempted
  dto.bytesSent = metrics.bytesSent
  dto.bluetoothFailureStage = wasSuccessful ? undefined : metrics.bluetoothFailureStage
  dto.gattConnectAttempts = metrics.gattConnectAttempts
  dto.gattReconnectAttempts = metrics.gattReconnectAttempts
  dto.gattOperationRetries = metrics.gattOperationRetries

  return dto
}

async function recordPrintLog(dto: LabelPrinterPrintLogCreateDto) {
  try {
    await recordLabelPrinterPrintLog(dto)
  } catch (error) {
    console.warn('Could not record Bluetooth label printer log:', error)
  }
}

function getWriteMethods(characteristic: WritableBluetoothCharacteristic) {
  const methods: WriteMethod[] = []
  const hasWriteWithResponse = typeof characteristic.writeValueWithResponse === 'function'
  const hasWriteWithoutResponse = typeof characteristic.writeValueWithoutResponse === 'function'
  const supportsWriteWithResponse = characteristic.properties?.write
  const supportsWriteWithoutResponse = characteristic.properties?.writeWithoutResponse

  if (hasWriteWithResponse && supportsWriteWithResponse !== false) {
    methods.push('with-response')
  }

  if (hasWriteWithoutResponse && supportsWriteWithoutResponse) {
    methods.push('without-response')
  }

  // Some Zebra firmware/browser combinations expose incomplete characteristic properties.
  if (hasWriteWithResponse && !methods.includes('with-response')) {
    methods.push('with-response')
  }

  if (hasWriteWithoutResponse && !methods.includes('without-response')) {
    methods.push('without-response')
  }

  methods.push('legacy')

  return methods
}

async function writePayload(
  characteristic: WritableBluetoothCharacteristic,
  method: WriteMethod,
  payload: ArrayBuffer
) {
  if (method === 'with-response' && characteristic.writeValueWithResponse) {
    await withTimeout(
      characteristic.writeValueWithResponse(payload),
      WRITE_TIMEOUT_MS,
      'Timed out while writing to the label printer.'
    )
    return
  }

  if (method === 'without-response' && characteristic.writeValueWithoutResponse) {
    await withTimeout(
      characteristic.writeValueWithoutResponse(payload),
      WRITE_TIMEOUT_MS,
      'Timed out while writing to the label printer.'
    )
    return
  }

  await withTimeout(
    characteristic.writeValue(payload),
    WRITE_TIMEOUT_MS,
    'Timed out while writing to the label printer.'
  )
}

async function writeChunk(
  characteristic: WritableBluetoothCharacteristic,
  chunk: Uint8Array,
  writeMethods: WriteMethod[]
) {
  const payload = new Uint8Array(chunk).buffer
  let lastError: unknown

  for (const method of writeMethods) {
    try {
      await writePayload(characteristic, method, payload)
      return method
    } catch (error) {
      lastError = error

      if (!isUnsupportedWriteError(error)) {
        throw new BluetoothWriteFailure(method, error)
      }
    }
  }

  throw lastError ?? new Error('The label printer does not support Bluetooth writes.')
}

async function cachePreviouslyAllowedPrinter(config: BluetoothLabelPrintingConfig) {
  const bluetooth = getBluetooth()

  if (shouldForceBluetoothDeviceSelection()) {
    bluetoothLabelPrinterState.cachedPrinterDevice = undefined
    bluetoothLabelPrinterState.selectedLabelPrinter = undefined
    return
  }

  if (isConfiguredLabelPrinter(bluetoothLabelPrinterState.cachedPrinterDevice, config)) {
    return
  }

  if (!bluetooth?.getDevices) {
    return
  }

  try {
    const devices = await bluetooth.getDevices()
    const printer = devices.find(device => isConfiguredLabelPrinter(device, config))

    if (printer) {
      bluetoothLabelPrinterState.cachedPrinterDevice = printer
      bluetoothLabelPrinterState.selectedLabelPrinter = getConfiguredLabelPrinterByDeviceName(config, printer.name)
    }
  } catch (error) {
    console.warn('Could not inspect previously allowed Bluetooth devices:', error)
  }
}

async function requestPrinterDevice(bluetooth: BluetoothNavigatorLike, config: BluetoothLabelPrintingConfig) {
  const shouldUseCachedDevice = !shouldForceBluetoothDeviceSelection()

  if (shouldUseCachedDevice && isConfiguredLabelPrinter(bluetoothLabelPrinterState.cachedPrinterDevice, config)) {
    return bluetoothLabelPrinterState.cachedPrinterDevice!
  }

  const configuredDeviceNames = getConfiguredPrinterDeviceNames(config)

  if (!configuredDeviceNames.length) {
    throw new Error('No active Bluetooth label printers are configured with a device name or serial number.')
  }

  const device = await bluetooth.requestDevice({
    filters: configuredDeviceNames.map(name => ({ name })),
    optionalServices: [LABEL_PRINTER_SERVICE_UUID]
  })

  bluetoothLabelPrinterState.cachedPrinterDevice = shouldUseCachedDevice ? device : undefined
  bluetoothLabelPrinterState.selectedLabelPrinter = getConfiguredLabelPrinterByDeviceName(config, device.name)

  return device
}

async function connectToGattServer(device: BluetoothDeviceLike, metrics: BluetoothPrintMetrics) {
  if (!device.gatt) {
    throw new Error('The selected Bluetooth device does not support GATT connections.')
  }

  if (device.gatt.connected) {
    return device.gatt
  }

  let lastError: unknown

  for (let attempt = 1; attempt <= MAX_CONNECT_ATTEMPTS; attempt += 1) {
    try {
      metrics.bluetoothFailureStage = 'connect'
      metrics.gattConnectAttempts += 1

      return await withTimeout(
        device.gatt.connect(),
        CONNECT_TIMEOUT_MS,
        'Timed out while connecting to the label printer.'
      )
    } catch (error) {
      lastError = error

      if (attempt < MAX_CONNECT_ATTEMPTS) {
        await sleep(250)
      }
    }
  }

  throw lastError ?? new Error('Could not connect to the label printer.')
}

async function getWritableBluetoothCharacteristic(device: BluetoothDeviceLike, metrics: BluetoothPrintMetrics) {
  const server = await connectToGattServer(device, metrics)
  metrics.bluetoothFailureStage = 'service-discovery'
  const service = await withTimeout(
    server.getPrimaryService(LABEL_PRINTER_SERVICE_UUID),
    GATT_DISCOVERY_TIMEOUT_MS,
    'Timed out while discovering the Zebra label printer service.'
  )
  metrics.bluetoothFailureStage = 'characteristic-discovery'

  return withTimeout(
    service.getCharacteristic(LABEL_PRINTER_CHARACTERISTIC_UUID),
    GATT_DISCOVERY_TIMEOUT_MS,
    'Timed out while discovering the Zebra label printer write characteristic.'
  )
}

function canRetryDisconnectedGattOperation(error: unknown, metrics: BluetoothPrintMetrics) {
  return (
    isGattDisconnectedError(getBluetoothWriteFailureError(error))
    && metrics.gattOperationRetries < GATT_DISCONNECTED_RETRY_DELAYS_MS.length
  )
}

async function waitBeforeDisconnectedGattRetry(device: BluetoothDeviceLike, metrics: BluetoothPrintMetrics) {
  const retryDelayMs = GATT_DISCONNECTED_RETRY_DELAYS_MS[metrics.gattOperationRetries]

  metrics.gattOperationRetries += 1
  metrics.gattReconnectAttempts += 1
  disconnectDevice(device)
  await sleep(retryDelayMs)
}

async function getWritableBluetoothCharacteristicWithRecovery(
  device: BluetoothDeviceLike,
  metrics: BluetoothPrintMetrics
) {
  while (true) {
    try {
      return await getWritableBluetoothCharacteristic(device, metrics)
    } catch (error) {
      if (!canRetryDisconnectedGattOperation(error, metrics)) {
        throw error
      }

      await waitBeforeDisconnectedGattRetry(device, metrics)
    }
  }
}

function getAvailableBluetooth() {
  const bluetooth = getBluetooth()

  if (!bluetooth) {
    throw new Error('Bluetooth printing is not available in this browser. Please use Chrome on Android or desktop Chrome.')
  }

  if (typeof window !== 'undefined' && !window.isSecureContext) {
    throw new Error('Bluetooth printing requires a secure HTTPS connection.')
  }

  return bluetooth
}

async function sendPrnViaBluetooth(
  prn: Uint8Array,
  config: BluetoothLabelPrintingConfig,
  device: BluetoothDeviceLike,
  metrics: BluetoothPrintMetrics
) {
  try {
    let characteristic = await getWritableBluetoothCharacteristicWithRecovery(device, metrics)
    let writeMethods = getWriteMethods(characteristic)
    await sleep(PRE_WRITE_SETTLE_MS)

    for (let i = 0; i < prn.length; i += BLE_CHUNK_SIZE) {
      const chunk = prn.subarray(i, i + BLE_CHUNK_SIZE)
      let successfulWriteMethod: WriteMethod | undefined

      while (!successfulWriteMethod) {
        try {
          metrics.bluetoothFailureStage = 'write'
          successfulWriteMethod = await writeChunk(characteristic, chunk, writeMethods)
        } catch (error) {
          const writeFailure = getBluetoothWriteFailure(error)
          const errorCause = getBluetoothWriteFailureError(error)

          metrics.failedChunkIndex = i / BLE_CHUNK_SIZE
          metrics.failedByteOffset = i

          if (!canRetryDisconnectedGattOperation(errorCause, metrics)) {
            throw errorCause
          }

          await waitBeforeDisconnectedGattRetry(device, metrics)
          characteristic = await getWritableBluetoothCharacteristicWithRecovery(device, metrics)
          const recoveredWriteMethods = getWriteMethods(characteristic)

          writeMethods = writeFailure
            ? [
              ...recoveredWriteMethods.filter((method) => method !== writeFailure.method),
              writeFailure.method
            ]
            : recoveredWriteMethods
          await sleep(PRE_WRITE_SETTLE_MS)
        }
      }

      metrics.failedChunkIndex = undefined
      metrics.failedByteOffset = undefined
      metrics.bytesSent += chunk.length

      if (!metrics.writeMethodUsed) {
        metrics.writeMethodUsed = successfulWriteMethod
      }

      if (successfulWriteMethod !== writeMethods[0]) {
        writeMethods = [
          successfulWriteMethod,
          ...writeMethods.filter((method) => method !== successfulWriteMethod)
        ]
      }

      // Zebra BLE printing is a byte stream; Android stacks can drop data if writes are too tight.
      if (i + BLE_CHUNK_SIZE < prn.length) {
        await sleep(INTER_CHUNK_DELAY_MS)
      }
    }

    // Let the printer consume the final bytes before the browser tears down the GATT connection.
    await sleep(POST_WRITE_SETTLE_MS)
  } finally {
    disconnectDevice(device)
  }
}

function createPrintMetrics(
  config: BluetoothLabelPrintingConfig,
  device: BluetoothDeviceLike,
  prn: Uint8Array
): BluetoothPrintMetrics {
  return {
    selectedBluetoothDeviceName: device.name,
    labelPrinter: getConfiguredLabelPrinterByDeviceName(config, device.name),
    totalChunks: Math.ceil(prn.length / BLE_CHUNK_SIZE),
    bytesAttempted: prn.length,
    bytesSent: 0,
    gattConnectAttempts: 0,
    gattReconnectAttempts: 0,
    gattOperationRetries: 0
  }
}

export async function printBluetoothLabelFromUserGesture(options: BluetoothLabelPrintOptions) {
  let selectionPromise: Promise<{
    config: BluetoothLabelPrintingConfig
    device: BluetoothDeviceLike
  }>

  try {
    const bluetooth = getAvailableBluetooth()
    const config = bluetoothLabelPrinterState.config

    if (!config) {
      initializeBluetoothLabelPrinterState().catch(error => {
        console.warn('Could not initialize Bluetooth label printer state:', error)
      })
      throw new Error('Label printer configuration is still loading. Please try again in a moment.')
    }

    selectionPromise = requestPrinterDevice(bluetooth, config).then(device => ({ config, device }))
  } catch (error) {
    alertUser(getErrorMessage(error))
    return
  }

  const printJob = bluetoothLabelPrinterState.printQueue.then(async () => {
    let config: BluetoothLabelPrintingConfig | undefined
    let prn: Uint8Array | undefined
    let metrics: Partial<BluetoothPrintMetrics> = {}

    try {
      const selectedPrinter = await selectionPromise
      config = selectedPrinter.config
      prn = await options.getPrn()

      if (!prn?.length) {
        return
      }

      const printMetrics = createPrintMetrics(config, selectedPrinter.device, prn)
      metrics = printMetrics
      await sendPrnViaBluetooth(prn, config, selectedPrinter.device, printMetrics)
      await recordPrintLog(buildPrintLog(options, config, prn, metrics, true))
    } catch (error) {
      if (isDomException(error) && error.name === 'NotFoundError') {
        return
      }

      bluetoothLabelPrinterState.cachedPrinterDevice = undefined
      console.error('Bluetooth printing failed:', error)

      if (config) {
        await recordPrintLog(buildPrintLog(options, config, prn, metrics, false, error))
      }

      alertUser(
        `Error: Could not print to the Zebra label printer via Bluetooth. Please make sure the printer is powered on, nearby, and not connected to another device.\n${getErrorMessage(error)}`
      )
    }
  })

  bluetoothLabelPrinterState.printQueue = printJob.catch(() => undefined)

  return printJob
}

function getEasternDateTimeLabel() {
  const now = new Date()
  const date = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'America/New_York',
    year: 'numeric'
  }).format(now)
  const time = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    hour12: true,
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/New_York'
  }).format(now)

  return `${date} ${time} ET`
}

function toAsciiBytes(value: string) {
  const bytes = new Uint8Array(value.length)

  for (let i = 0; i < value.length; i += 1) {
    bytes[i] = value.charCodeAt(i) & 0xff
  }

  return bytes
}

export function createBluetoothTestLabelPrn() {
  const timestamp = getEasternDateTimeLabel()

  return toAsciiBytes(
    `! 0 200 200 406 1
PW 812
TONE 0
SPEED 3
ON-FEED IGNORE
NO-PACE
BAR-SENSE
T 5 2 27 90 This is a test label
T 5 0 27 150 ${timestamp}
PRINT`
  )
}

export async function printBluetoothTestLabelFromUserGesture() {
  return printBluetoothLabelFromUserGesture({
    getPrn: async () => createBluetoothTestLabelPrn()
  })
}

export async function connectAndSendViaBluetooth(prn: Uint8Array) {
  if (!prn?.length) {
    return
  }

  return printBluetoothLabelFromUserGesture({
    getPrn: async () => prn
  })
}
